import React from "react";
import {reduxForm, getFormMeta, getFormValues, autofill, getFormInitialValues} from "redux-form";
import {connect} from 'react-redux'
import getRegisteredFields from "./getRegisteredFields";
import AutofillApi from "./../api/gen/uzn/AutofillApi";

function loadData(context) {
    return AutofillApi.load([context]).then(data => {
        return data && data[context];
    });
}

function loadAndFillData(formName, context, onlyFields = null) {
    return (dispatch, getStore) => {
        loadData(context).then(data => {
            if (!data) {
                return;
            }

            const state = getStore();

            const fields = getRegisteredFields(formName)(state);
            const meta = getFormMeta(formName)(state) || {};
            const initialValues = getFormInitialValues(formName)(state) || {};
            const values = getFormValues(formName)(state) || {};

            const autofillField = (field) => {
                const fieldMeta = meta[field];
                const fieldValue = values[field];
                const fieldInitialValue = initialValues[field];

                const itIsNotInitialValue = fieldInitialValue ? fieldInitialValue !== fieldValue : fieldValue;

                if ((fieldMeta && fieldMeta.active && itIsNotInitialValue) || (fieldMeta && !fieldMeta.autofilled)) {
                    return;
                }
                dispatch(autofill(formName, field, data[field]));
            };

            if (onlyFields !== null) {
                for(let n in fields) {
                    fields.hasOwnProperty(n) && onlyFields.indexOf(n) !== -1 && autofillField(n);
                }
            } else {
                for(let n in fields) {
                    fields.hasOwnProperty(n) && autofillField(n);
                }
            }
        })
    }
}


function saveData(formName, context, onlyFields = null) {
    return (dispatch, getStore) => {
        const state = getStore();
        const values = getFormValues(formName)(state) || null;
        if (values !== null) {
            let dataToSave = {};
            if (onlyFields !== null) {
                for (let x in values) {
                    if (values.hasOwnProperty(x) && onlyFields.indexOf(x) !== -1) {
                        dataToSave[x] = values[x];
                    }
                }
            } else {
                dataToSave = values;
            }

            AutofillApi.save({[context]: dataToSave});
        }
    }
}

/**
 * High-order komponenta vytvářející formulář s podporou autofill.
 *
 * Ve formuláři budou dostupné následující properties:
 *
 *   loadAutofill(fields: Array<string>): void - Funkce, která zajistí načtení dat a vyplnění specifických polí formuláře.
 *                                               Při neuvedení seznamu polí se použijí pole z konfigurace.
 *
 *   loadAutofillData(): Promise<Object> - Low-level funkce, která pouze načte data pro vyplnění polí.
 *
 * @param config - konfigurace
 * @param {string} [config.autofillContext] - Kontext. Při neuvedení bude bude automatické naplňování/ukládání zakázáno.
 *                                            Alternativně lze kontext předat přes stejně pojmenovanou property.
 * @param {Array<string>} [config.autofillFields] - Pole, která se mají automaticky naplňovat/ukládat.
 *                                                  Při neuvedení se použíjí všechna pole formuláře.
 * @param {Array<string>} [config.initAutofillFields] - Podmnožina autofillFields, která se má naplnit při prvotní inicializaci.
 *                                                      Při neuvedení se použijí všechna pole autofillFields, případně všechna pole formuláře.
 * @returns {function} výsledná komponenta
 */
export default function autofillReduxForm(config) {
    const {autofillContext, autofillFields, initAutofillFields, ...restConfig} = config;

    function getContext(props) {
        return props.autofillContext || autofillContext;
    }

    function requireContext(props) {
        const context = getContext(props);
        if (context) {
            return context;
        }
        throw new Error('Nenastaven autofillContext');
    }

    const mapDispatchToProps = (dispatch, props) => ({
        loadAutofill: fields => dispatch(loadAndFillData(restConfig.form, requireContext(props), fields || autofillFields)),
        loadAutofillData: () => loadData(requireContext(props)),
    });

    return ComponentToWrap => connect(undefined, mapDispatchToProps)(class AutofillReduxForm extends React.Component {
        x = null;

        constructor(props) {
            super(props);
            const {onSubmitSuccess} = props;
            let context = getContext(props);
            this.x = reduxForm(
                {
                    ...restConfig,
                    onSubmitSuccess: (result, dispatch, props) => {
                        context && dispatch(saveData(restConfig.form, context, autofillFields));
                        if (onSubmitSuccess) {
                            onSubmitSuccess(result, dispatch, props);
                        } else if (config.onSubmitSuccess) {
                            config.onSubmitSuccess(result, dispatch, props);
                        }
                    }
                }
            )(ComponentToWrap);
        }

        componentDidMount() {
            if (getContext(this.props)) {
                this.props.loadAutofill(this.props.initAutofillFields || initAutofillFields);
            }
        }

        submit() {
            return this.refs.wrapped.getWrappedInstance().submit()
        }

        reset() {
            return this.refs.wrapped.getWrappedInstance().reset()
        }

        get valid() {
            return this.refs.wrapped.getWrappedInstance().isValid()
        }

        get invalid() {
            return !this.valid
        }

        get pristine() {
            return this.refs.wrapped.getWrappedInstance().isPristine()
        }

        get dirty() {
            return !this.pristine
        }

        get values() {
            return this.refs.wrapped.getWrappedInstance().getValues()
        }

        get wrappedInstance() {
            // for testing
            return this.refs.wrapped.getWrappedInstance().refs.wrapped
        }

        render() {
            const {onSubmitSuccess, ...rest} = this.props;
            return React.createElement(
                this.x,
                {
                    ...rest,
                    ref: 'wrapped',
                }
            )
        }
    })

}
